import { Injectable } from "@angular/core";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";

import { Observable, catchError, map, shareReplay } from "rxjs";

import { environment } from "src/environments/environment";
import { BaseService } from "../base/base.service";
import { EGenericIdentifier } from "../interfaces/generic.interface";
import { ISelectorModel } from "../interfaces/selector.interface";

/** Responsible for making the generic manipulation methods available by the main api */
@Injectable({ providedIn: "root" })
export class GenericService extends BaseService {
	/** Api Url */
	protected readonly apiUrl!: string;

	/**
	 * Each identifier's list, kept for the lifetime of the session — see {@link list}.
	 */
	private readonly lists = new Map<EGenericIdentifier, Observable<ISelectorModel[]>>();

	/** @ignore */
	constructor(protected readonly http: HttpClient) {
		super();
		this.apiUrl = `${environment.mainApiUrl}generic/`;
	}

	/**
	 * Return generic list in selector format.
	 *
	 * Each identifier is fetched once and reused from then on. These are domain lists — periods,
	 * situations, commodity groups — that are read on nearly every screen and change very rarely, so
	 * refetching them was spending requests on answers already known: the Actions screen alone asked
	 * for six of them on open, and every return to it asked again.
	 *
	 * Sharing the same observable also collapses concurrent callers into a single request, which
	 * matters because screens request several of these at once, on open.
	 *
	 * A failed request is dropped from the cache so the next caller retries rather than inheriting
	 * the error forever.
	 * @param {EGenericIdentifier} identifier - Generic identifier
	 * @returns Generic list as ISelectorModel[]
	 */
	list = (identifier: EGenericIdentifier): Observable<ISelectorModel[]> => {
		const cached = this.lists.get(identifier);
		if (cached) return cached;

		const generics$ = this.http.get(this.apiUrl + 'list?identifier=' + identifier).pipe(
			map((generics) => generics as ISelectorModel[]),
			catchError((error: HttpErrorResponse) => {
				this.lists.delete(identifier);
				return super.serviceError(error);
			}),
			shareReplay({ bufferSize: 1, refCount: false })
		);

		this.lists.set(identifier, generics$);

		return generics$;
	};

	/** Discards the cached lists, so the next request for each one goes to the server again. */
	clearCache(): void {
		this.lists.clear();
	}
}
